import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, Upload, AlertCircle, CheckCircle } from 'lucide-react';
import { getPillars, getThemes, getSubThemes } from '../data/taxonomy';
import { API_BASE_URL } from '../config/api';

export default function UploadPaper() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    pillar: '',
    theme: '',
    sub_themes: [],
    authors_list: '',  // Comma-separated author IDs
    publication_date: '',
    price: '',
    keywords: '',
    // Curation fields
    abstract: '',
    declaration_of_originality: false,
    plagiarism_similarity: '',
    relevance_score: '',
    analytical_depth_score: '',
    practical_applicability_score: '',
    review_summary: ''
    // Status removed - auto-published
  });
  
  const [pdfFile, setPdfFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [availableThemes, setAvailableThemes] = useState([]);
  const [availableSubThemes, setAvailableSubThemes] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  const pillars = getPillars();

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const { data } = await axios.get(`${API_BASE_URL}/api/authors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuthors(data);
    } catch (error) {
      console.error('Error fetching authors:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'pillar') {
      setFormData({ ...formData, pillar: value, theme: '', sub_themes: [] });
      setAvailableThemes(getThemes(value));
      setAvailableSubThemes([]);
    }
    else if (name === 'theme') {
      setFormData({ ...formData, theme: value, sub_themes: [] });
      setAvailableSubThemes(getSubThemes(formData.pillar, value));
    }
    else if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    }
    else {
      setFormData({ ...formData, [name]: value });
      
      // Real-time validation
      validateField(name, value);
    }
  };

  const handleSubThemeToggle = (subTheme) => {
    const currentSubThemes = formData.sub_themes;
    if (currentSubThemes.includes(subTheme)) {
      setFormData({ 
        ...formData, 
        sub_themes: currentSubThemes.filter(st => st !== subTheme) 
      });
    } else {
      setFormData({ 
        ...formData, 
        sub_themes: [...currentSubThemes, subTheme] 
      });
    }
  };

  const validateField = (name, value) => {
    const errors = { ...validationErrors };

    switch (name) {
      case 'abstract':
        const wordCount = value.trim().split(/\s+/).filter(w => w.length > 0).length;
        if (value && (wordCount < 150 || wordCount > 200)) {
          errors.abstract = `Abstract must be 150-200 words (current: ${wordCount})`;
        } else {
          delete errors.abstract;
        }
        break;

      case 'plagiarism_similarity':
        const plagiarism = parseFloat(value);
        if (value && (isNaN(plagiarism) || plagiarism < 0 || plagiarism > 100)) {
          errors.plagiarism_similarity = 'Must be between 0-100%';
        } else if (value && plagiarism > 15) {
          errors.plagiarism_similarity = 'Plagiarism similarity must be ≤15%';
        } else {
          delete errors.plagiarism_similarity;
        }
        break;

      case 'relevance_score':
      case 'analytical_depth_score':
      case 'practical_applicability_score':
        const score = parseInt(value);
        if (value && (isNaN(score) || score < 1 || score > 10)) {
          errors[name] = 'Score must be between 1-10';
        } else {
          delete errors[name];
        }
        break;

      default:
        break;
    }

    setValidationErrors(errors);
  };

  const calculateTotalScore = () => {
    const relevance = parseInt(formData.relevance_score) || 0;
    const depth = parseInt(formData.analytical_depth_score) || 0;
    const applicability = parseInt(formData.practical_applicability_score) || 0;
    return relevance + depth + applicability;
  };

  const getRatingBand = (totalScore) => {
    if (totalScore >= 25) return { label: 'Flagship 🏆', color: 'text-yellow-600' };
    if (totalScore >= 20) return { label: 'Strong ⭐', color: 'text-blue-600' };
    if (totalScore >= 15) return { label: 'Moderate ✓', color: 'text-green-600' };
    return { label: 'Not Recommended', color: 'text-gray-600' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate all fields
    if (Object.keys(validationErrors).length > 0) {
      setError('Please fix all validation errors before submitting');
      return;
    }

    if (!pdfFile) {
      setError('Please select a PDF file');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('access_token');
      
      // Create FormData for file upload
      const uploadData = new FormData();
      uploadData.append('title', formData.title);
      uploadData.append('description', formData.description);
      uploadData.append('pillar', formData.pillar);
      uploadData.append('theme', formData.theme);
      uploadData.append('sub_themes', formData.sub_themes.join(', '));
      uploadData.append('authors_list', formData.authors_list);
      uploadData.append('price', formData.price || '0');
      uploadData.append('keywords', formData.keywords);
      if (formData.publication_date) {
        uploadData.append('publication_date', formData.publication_date);
      }
      
      // Curation fields
      uploadData.append('abstract', formData.abstract);
      uploadData.append('declaration_of_originality', formData.declaration_of_originality);
      uploadData.append('plagiarism_similarity', parseFloat(formData.plagiarism_similarity) || 0);
      uploadData.append('relevance_score', parseInt(formData.relevance_score));
      uploadData.append('analytical_depth_score', parseInt(formData.analytical_depth_score));
      uploadData.append('practical_applicability_score', parseInt(formData.practical_applicability_score));
      uploadData.append('review_summary', formData.review_summary);
      uploadData.append('status', 'published');  // Auto-publish all uploads
      
      uploadData.append('pdf_file', pdfFile);

      const response = await axios.post(
        `${API_BASE_URL}/api/papers/upload-with-file`,
        uploadData,
        { 
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('Paper uploaded successfully:', response.data);
      navigate('/admin');
    } catch (err) {
      console.error('Upload error:', err);
      
      if (err.response?.status === 422 && Array.isArray(err.response?.data?.detail)) {
        const errorMessages = err.response.data.detail
          .map(e => `${e.loc.join(' → ')}: ${e.msg}`)
          .join(', ');
        setError(`Validation error: ${errorMessages}`);
      } else {
        setError(err.response?.data?.detail || 'Failed to upload paper. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const totalScore = calculateTotalScore();
  const ratingBand = getRatingBand(totalScore);
  const abstractWordCount = formData.abstract.trim().split(/\s+/).filter(w => w.length > 0).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-[#E87722] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </button>

        <Card className="p-8 max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Upload className="h-8 w-8 text-[#E87722]" />
            <h1 className="text-3xl font-bold text-[#1E3A5F]">Upload New Research Paper</h1>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md flex items-start gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-[#1E3A5F] border-b pb-2">Basic Information</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Title */}
                <div className="md:col-span-2">
                  <Label htmlFor="title">Paper Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="Enter paper title"
                    className="mt-1"
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <Label htmlFor="description">Description *</Label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent"
                    placeholder="Brief description of the research paper"
                  />
                </div>

                {/* Pillar */}
                <div>
                  <Label htmlFor="pillar">Pillar *</Label>
                  <select
                    id="pillar"
                    name="pillar"
                    value={formData.pillar}
                    onChange={handleChange}
                    required
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent"
                  >
                    <option value="">Select pillar</option>
                    {pillars.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>

                {/* Theme */}
                <div>
                  <Label htmlFor="theme">Theme *</Label>
                  <select
                    id="theme"
                    name="theme"
                    value={formData.theme}
                    onChange={handleChange}
                    required
                    disabled={!formData.pillar}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent disabled:bg-gray-100"
                  >
                    <option value="">Select theme</option>
                    {availableThemes.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  {!formData.pillar && (
                    <p className="text-xs text-gray-500 mt-1">Select a pillar first</p>
                  )}
                </div>

                {/* Sub-themes */}
                <div className="md:col-span-2">
                  <Label>Sub-themes (select multiple)</Label>
                  {availableSubThemes.length > 0 ? (
                    <div className="mt-2 border border-gray-300 rounded-md p-4 max-h-64 overflow-y-auto bg-white">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {availableSubThemes.map(st => (
                          <label key={st} className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={formData.sub_themes.includes(st)}
                              onChange={() => handleSubThemeToggle(st)}
                              className="mt-1 rounded border-gray-300 text-[#E87722] focus:ring-[#E87722]"
                            />
                            <span className="text-sm text-gray-700">{st}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500 py-4 px-4 bg-gray-50 rounded-md border border-gray-200">
                      {formData.theme ? 'No sub-themes available' : 'Select a theme to see sub-themes'}
                    </p>
                  )}
                  {formData.sub_themes.length > 0 && (
                    <p className="text-xs text-[#E87722] font-medium mt-2">
                      ✓ Selected: {formData.sub_themes.length} sub-theme{formData.sub_themes.length !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>

                {/* Authors */}
                <div className="md:col-span-2">
                  <div className="flex justify-between items-center mb-2">
                    <Label htmlFor="authors_list">Select Authors *</Label>
                    <a 
                      href="/admin/authors" 
                      target="_blank"
                      className="text-xs text-[#E87722] hover:underline"
                    >
                      + Manage Authors
                    </a>
                  </div>
                  
                  {/* Author Profile Cards */}
                  {authors.length > 0 ? (
                    <div className="space-y-3 border border-gray-300 rounded-md p-4 bg-gray-50 max-h-80 overflow-y-auto">
                      {authors.map((author) => (
                        <label 
                          key={author.id}
                          className="flex items-start gap-3 p-3 border border-gray-200 bg-white rounded-md cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={formData.authors_list.includes(author.id)}
                            onChange={() => {
                              const currentIds = formData.authors_list.split(',').filter(id => id.trim());
                              if (currentIds.includes(author.id)) {
                                // Remove author
                                const newIds = currentIds.filter(id => id !== author.id);
                                setFormData({ ...formData, authors_list: newIds.join(', ') });
                              } else {
                                // Add author
                                const newIds = [...currentIds, author.id];
                                setFormData({ ...formData, authors_list: newIds.join(', ') });
                              }
                            }}
                            className="mt-1 rounded border-gray-300 text-[#E87722] focus:ring-[#E87722]"
                          />
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{author.name}</div>
                            <div className="text-sm text-[#E87722] mt-0.5">{author.credentials}</div>
                            <div className="text-sm text-gray-600 mt-0.5">{author.affiliation}</div>
                            <div className="text-xs text-gray-500 mt-1 line-clamp-2">{author.bio}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 border border-gray-300 rounded-md bg-gray-50 text-center">
                      <p className="text-sm text-red-600 mb-2">
                        No authors available. Please add authors first.
                      </p>
                      <a 
                        href="/admin/authors" 
                        target="_blank"
                        className="text-sm text-[#E87722] hover:underline"
                      >
                        → Go to Author Management
                      </a>
                    </div>
                  )}
                  
                  {formData.authors_list && (
                    <p className="text-xs text-green-600 font-medium mt-2">
                      ✓ Selected: {formData.authors_list.split(',').filter(id => id.trim()).length} author(s)
                    </p>
                  )}
                </div>

                {/* Publication Date */}
                <div>
                  <Label htmlFor="publication_date">Publication Date</Label>
                  <Input
                    id="publication_date"
                    name="publication_date"
                    type="date"
                    value={formData.publication_date}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>

                {/* Keywords */}
                <div className="md:col-span-2">
                  <Label htmlFor="keywords">Keywords (comma-separated)</Label>
                  <Input
                    id="keywords"
                    name="keywords"
                    value={formData.keywords}
                    onChange={handleChange}
                    placeholder="e.g., MSME, Economic Recovery, India"
                    className="mt-1"
                  />
                </div>

                {/* PDF File Upload */}
                <div className="md:col-span-2">
                  <Label htmlFor="pdf_file">Upload PDF File *</Label>
                  <input
                    id="pdf_file"
                    name="pdf_file"
                    type="file"
                    accept=".pdf"
                    required
                    onChange={(e) => setPdfFile(e.target.files[0])}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#E87722] file:text-white hover:file:bg-[#d66a1a] file:cursor-pointer"
                  />
                  {pdfFile && (
                    <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" />
                      Selected: {pdfFile.name} ({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Curation & Review Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-[#1E3A5F] border-b pb-2">Research Curation & Review</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Abstract */}
                <div className="md:col-span-2">
                  <div className="flex justify-between items-center mb-1">
                    <Label htmlFor="abstract">Abstract (150-200 words) *</Label>
                    <span className={`text-xs font-medium ${
                      abstractWordCount < 150 || abstractWordCount > 200 
                        ? 'text-red-600' 
                        : 'text-green-600'
                    }`}>
                      {abstractWordCount} / 150-200 words
                    </span>
                  </div>
                  <textarea
                    id="abstract"
                    name="abstract"
                    value={formData.abstract}
                    onChange={handleChange}
                    required
                    rows={6}
                    className={`mt-1 w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent ${
                      validationErrors.abstract ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter paper abstract (150-200 words)"
                  />
                  {validationErrors.abstract && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {validationErrors.abstract}
                    </p>
                  )}
                </div>

                {/* Declaration of Originality */}
                <div className="md:col-span-2">
                  <label className="flex items-start space-x-3 cursor-pointer p-4 border border-gray-300 rounded-md hover:bg-gray-50">
                    <input
                      type="checkbox"
                      name="declaration_of_originality"
                      checked={formData.declaration_of_originality}
                      onChange={handleChange}
                      required
                      className="mt-1 rounded border-gray-300 text-[#E87722] focus:ring-[#E87722]"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Originality Declared *</span>
                      <p className="text-sm text-gray-600 mt-1">
                        Confirm that the author's signed declaration of originality has been received offline.
                      </p>
                    </div>
                  </label>
                </div>

                {/* Plagiarism Similarity */}
                <div>
                  <Label htmlFor="plagiarism_similarity">Plagiarism Similarity (%) *</Label>
                  <Input
                    id="plagiarism_similarity"
                    name="plagiarism_similarity"
                    type="number"
                    step="0.1"
                    value={formData.plagiarism_similarity}
                    onChange={handleChange}
                    required
                    placeholder="0-15"
                    className={`mt-1 ${validationErrors.plagiarism_similarity ? 'border-red-500' : ''}`}
                  />
                  {validationErrors.plagiarism_similarity && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {validationErrors.plagiarism_similarity}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum allowed: 15%. Manually check using Grammarly/Turnitin.
                  </p>
                </div>

                {/* Review Scores */}
                <div className="md:col-span-2 space-y-4 p-4 bg-blue-50 rounded-md border border-blue-200">
                  <h3 className="font-bold text-[#1E3A5F]">Review Scoring (30 points total)</h3>
                  
                  {/* Relevance Score */}
                  <div>
                    <Label htmlFor="relevance_score">1. Relevance to CSED Pillars (1-10) *</Label>
                    <Input
                      id="relevance_score"
                      name="relevance_score"
                      type="number"
                      min="1"
                      max="10"
                      value={formData.relevance_score}
                      onChange={handleChange}
                      required
                      placeholder="1-10"
                      className={`mt-1 ${validationErrors.relevance_score ? 'border-red-500' : ''}`}
                    />
                    {validationErrors.relevance_score && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors.relevance_score}</p>
                    )}
                  </div>

                  {/* Analytical Depth Score */}
                  <div>
                    <Label htmlFor="analytical_depth_score">2. Analytical Depth & Rigor (1-10) *</Label>
                    <Input
                      id="analytical_depth_score"
                      name="analytical_depth_score"
                      type="number"
                      min="1"
                      max="10"
                      value={formData.analytical_depth_score}
                      onChange={handleChange}
                      required
                      placeholder="1-10"
                      className={`mt-1 ${validationErrors.analytical_depth_score ? 'border-red-500' : ''}`}
                    />
                    {validationErrors.analytical_depth_score && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors.analytical_depth_score}</p>
                    )}
                  </div>

                  {/* Practical Applicability Score */}
                  <div>
                    <Label htmlFor="practical_applicability_score">3. Practical Applicability (1-10) *</Label>
                    <Input
                      id="practical_applicability_score"
                      name="practical_applicability_score"
                      type="number"
                      min="1"
                      max="10"
                      value={formData.practical_applicability_score}
                      onChange={handleChange}
                      required
                      placeholder="1-10"
                      className={`mt-1 ${validationErrors.practical_applicability_score ? 'border-red-500' : ''}`}
                    />
                    {validationErrors.practical_applicability_score && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors.practical_applicability_score}</p>
                    )}
                  </div>

                  {/* Total Score Display */}
                  {(formData.relevance_score || formData.analytical_depth_score || formData.practical_applicability_score) && (
                    <div className="p-3 bg-white rounded-md border border-blue-300">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-[#1E3A5F]">Total Score:</span>
                        <span className="text-2xl font-bold text-[#E87722]">{totalScore} / 30</span>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-sm text-gray-600">Rating Band:</span>
                        <span className={`font-bold ${ratingBand.color}`}>{ratingBand.label}</span>
                      </div>
                      {totalScore >= 25 && (
                        <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          This paper qualifies for "Expert Reviewed" badge
                        </p>
                      )}
                      {totalScore < 20 && (
                        <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Note: Minimum score 20 recommended for publication
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Review Summary */}
                <div className="md:col-span-2">
                  <Label htmlFor="review_summary">Review Summary *</Label>
                  <textarea
                    id="review_summary"
                    name="review_summary"
                    value={formData.review_summary}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent"
                    placeholder="Brief summary of the review and key findings..."
                  />
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-4 border-t">
              <button
                type="submit"
                disabled={submitting || Object.keys(validationErrors).length > 0}
                className="px-6 py-3 bg-[#E87722] hover:bg-[#d66a1a] text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center min-w-[160px]"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Uploading...
                  </>
                ) : (
                  'Upload Paper'
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin')}
                disabled={submitting}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>

            <p className="text-sm text-gray-500">
              * Required fields. All papers are automatically published. Papers with score ≥25 receive "Expert Reviewed" badge.
            </p>
          </form>
        </Card>
      </div>

      <Footer />
    </div>
  );
}
